import { useEffect } from 'react';

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useSession } from 'next-auth/react';

import { Features } from '@lessonPlanner/constants';

import { routes } from '../constants/routes';
import { logAdBlock } from '../utils/server-side/adBlock';

interface Props {
  featureFlag?: Features;
}

export default function AdBlockDetected({ featureFlag }: Props) {
  const { data: session } = useSession();
  const email = session?.user?.email;

  useEffect(() => {
    logAdBlock({ featureFlag, email });
  }, [featureFlag, email]);

  return (
    <div className="flex min-h-screen flex-col">
      <div className="mt-auto flex flex-1 items-center justify-center p-6">
        <div className="max-w-3xl">
          <div className="flex items-center gap-4">
            <ExclamationTriangleIcon className="h-10 w-10 text-yellow-400" />
            <h1 className="text-3xl font-semibold text-[#212121]">
              Your browser is having trouble accessing this page
            </h1>
          </div>
          <div className="mt-8 space-y-6">
            <p className="text-lg text-[#6A7280]">
              Make sure you are using Chrome and check if any ad blockers are
              turned off.
            </p>
            <p className="text-lg text-[#6A7280]">
              Still not working? Contact us at{' '}
              <a
                className="text-denimBlue hover:underline"
                href="mailto:help@lessonplanner.ph"
              >
                help@lessonplanner.ph
              </a>
            </p>
          </div>
          <div className="mt-8">
            <button
              className="bg-denimBlue inline-flex items-center justify-center rounded-md px-8 py-3 text-lg font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              type="button"
              onClick={() => window.location.reload()}
            >
              Refresh page
            </button>
          </div>
        </div>
      </div>

      <div className="mt-auto flex w-full flex-col-reverse items-center py-3 text-base text-slate-600 sm:flex-row sm:justify-between sm:py-4 sm:px-20 md:px-10 lg:py-4 lg:px-20">
        <p>© Lesson Planner {new Date().getFullYear()}</p>
        <div className="flex">
          <Link
            className="hover:underline"
            href={routes.TERMS_AND_CONDITIONS}
            target="_blank"
          >
            Terms & Conditions
          </Link>
          <p className="mx-2">•</p>
          <Link
            className="hover:underline"
            href={routes.PRIVACY_POLICY}
            target="_blank"
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
